import {
  Alert,
  Button,
  Dialog,
  PinInput,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AlertCircle, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

const EMPTY_VALUE = ["", "", "", "", "", ""];

interface OtpApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (otp: string) => void;
  hasTotpSecret: boolean;
  isLoading: boolean;
  error?: string | null;
}

export default function OtpApprovalModal({
  isOpen,
  onClose,
  onConfirm,
  hasTotpSecret,
  isLoading,
  error,
}: OtpApprovalModalProps) {
  const [value, setValue] = useState<string[]>(EMPTY_VALUE);
  const otp = value.join("");

  useEffect(() => {
    if (error) setValue(EMPTY_VALUE);
  }, [error]);

  useEffect(() => {
    if (!isOpen) setValue(EMPTY_VALUE);
  }, [isOpen]);

  const handleClose = () => {
    setValue(EMPTY_VALUE);
    onClose();
  };

  const handleConfirm = () => {
    if (otp.length === 6) onConfirm(otp);
  };

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={(e) => !e.open && handleClose()}
      size="sm"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottom="1px solid #ebecef80" p={4}>
              <Dialog.Title fontSize="lg" fontWeight="semibold">
                {hasTotpSecret
                  ? "Clave Dinámica"
                  : "Configuración requerida"}
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger position="top-end" top={3} />
            <Dialog.Body py={6}>
              {hasTotpSecret ? (
                <VStack gap={4} align="stretch">
                  <Text color="gray.600" fontSize="sm">
                    Ingresa tu Clave Dinámica de 6 dígitos para aprobar esta
                    operación.
                  </Text>
                  <VStack gap={2}>
                    <PinInput.Root
                      size="lg"
                      otp
                      value={value}
                      onValueChange={(details) => setValue(details.value)}
                    >
                      <PinInput.HiddenInput />
                      <PinInput.Control>
                        {[0, 1, 2, 3, 4, 5].map((index) => (
                          <PinInput.Input key={index} index={index} />
                        ))}
                      </PinInput.Control>
                    </PinInput.Root>
                  </VStack>
                  {error && (
                    <Alert.Root status="error" colorPalette="red">
                      <Alert.Indicator>
                        <AlertCircle size={16} />
                      </Alert.Indicator>
                      <Alert.Content>
                        <Alert.Description fontSize="sm">
                          {error}
                        </Alert.Description>
                      </Alert.Content>
                    </Alert.Root>
                  )}
                </VStack>
              ) : (
                <VStack gap={4} align="stretch">
                  <Alert.Root status="warning" colorPalette="orange">
                    <Alert.Indicator>
                      <ShieldCheck size={16} />
                    </Alert.Indicator>
                    <Alert.Content>
                      <Alert.Title fontSize="sm" fontWeight="medium">
                        Clave Dinámica no configurada
                      </Alert.Title>
                    </Alert.Content>
                  </Alert.Root>
                  <Text color="gray.600" fontSize="sm">
                    Tu cuenta requiere Clave Dinámica para aprobar pagos, pero
                    aún no la tienes configurada. Contacta a Palomma para
                    configurar tu Clave Dinámica.
                  </Text>
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80" p={4}>
              <Button variant="ghost" onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              {hasTotpSecret && (
                <Button
                  colorPalette="palomma"
                  onClick={handleConfirm}
                  loading={isLoading}
                  disabled={otp.length !== 6}
                >
                  Verificar
                </Button>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
